/**
 * The Help Desk's fix card (#1947 Phases 2–3), as approved in the mockup.
 *
 *   safe     green "● Fix the serial": before → after, the proof, Do it /
 *            Cancel. After: "✓ Fixed. … Undo" (24 hours) and a blue Next line.
 *   confirm  amber: exactly who gets the email and what's attached; type the
 *            job number to confirm.
 *   admin    amber "▲ Needs Corbin or an Admin": Send for OK, then it waits
 *            in the Review list (no bell). This card and My questions show ✓
 *            when it's OK'd. An Admin who asks gets Do it.
 *
 * Nothing on the card is code or an id. The fix runs on the server through
 * the page's own route under the sign-in of whoever presses the button; this
 * card only asks.
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, ApiError } from "@/lib/api";

export interface ActionPreview {
    title: string;
    lines: string[];
    change?: { label: string; from: string | null; to: string };
    proof?: string;
    note?: string;
    undoable: boolean;
    cantUndo?: string;
}

export interface ActionCard {
    id: string;
    kind: string;
    tier: "safe" | "confirm" | "admin" | string;
    status: "proposed" | "waiting" | "done" | "failed" | "cancelled" | "declined" | "undone" | string;
    preview: ActionPreview;
    result: { text: string; next?: string } | null;
    error: string | null;
    undoUntil: string | null;
    canUndo: boolean;
    confirmLength?: number | null;
    askerIsAdmin?: boolean;
    approver?: string | null;
    decidedBy?: string | null;
    decisionNote?: string | null;
}

/** "**Jason Gosnell**" → bold; React nodes only, never HTML. */
export function Bold({ text }: { text: string }) {
    const parts = text.split(/(\*\*[^*]+\*\*)/g).filter((p) => p.length > 0);
    return (
        <>
            {parts.map((p, i) =>
                p.startsWith("**") && p.endsWith("**") ? <b key={i}>{p.slice(2, -2)}</b> : <span key={i}>{p}</span>,
            )}
        </>
    );
}

/** One line for the model's next turn, so it knows what the card did. */
export function actionNote(card: ActionCard): string {
    const what = card.preview.title;
    if (card.status === "done") return `[Card "${what}": done. ${card.result?.text ?? ""}]`;
    if (card.status === "undone") return `[Card "${what}": undone. ${card.result?.text ?? ""}]`;
    if (card.status === "failed") return `[Card "${what}": it didn't work. ${card.error ?? ""}]`;
    if (card.status === "cancelled") return `[Card "${what}": cancelled.]`;
    if (card.status === "waiting")
        return `[Card "${what}": sent for an OK; waiting on ${card.approver ?? "an Admin"}.]`;
    if (card.status === "declined") return `[Card "${what}": declined. ${card.decisionNote ?? ""}]`;
    return `[Card "${what}": waiting for the person to press the button.]`;
}

/** The card's change, preview lines, proof and notes (shared with the Review list). */
export function ActionPreviewBody({ card }: { card: ActionCard }) {
    const p = card.preview;
    return (
        <>
            {p.change && (
                <div className="hact-chg">
                    <span>{p.change.label}</span>
                    {p.change.from ? <s>{p.change.from}</s> : null}
                    <span className="hact-arrow" aria-hidden>
                        {p.change.from ? "→" : "+"}
                    </span>
                    <ins>{p.change.to.replace(/^\+/, "")}</ins>
                </div>
            )}
            {p.lines.map((l, i) => (
                <div key={i}>
                    <Bold text={l} />
                </div>
            ))}
            {p.proof && (
                <div className="hact-proof">
                    <span className="hact-pdf" aria-hidden />
                    <span>{p.proof}</span>
                </div>
            )}
            {p.note && <div className="hact-note">{p.note}</div>}
        </>
    );
}

export function HelpDeskActionCard({
    card,
    onChange,
    onNext,
}: {
    card: ActionCard;
    onChange: (card: ActionCard) => void;
    /** A follow-up the person can tap ("Resend the packet"). */
    onNext?: (text: string) => void;
}) {
    const [busy, setBusy] = useState<null | "run" | "undo" | "cancel">(null);
    const [note, setNote] = useState<string | null>(null);
    const [typed, setTyped] = useState("");
    const p = card.preview;
    const high = card.tier === "admin" || card.tier === "confirm";
    const sendForOk = card.tier === "admin" && !card.askerIsAdmin;

    // Waiting on an Admin: look again now and then, so this chat shows ✓
    // when it's OK'd. No bell, no push — just this card and My questions.
    useEffect(() => {
        if (card.status !== "waiting") return;
        const t = window.setInterval(() => {
            api.get<{ card: ActionCard }>(`/api/help-desk/actions/${card.id}`)
                .then((r) => {
                    if (r.card.status !== "waiting") onChange(r.card);
                })
                .catch(() => {});
        }, 20_000);
        return () => window.clearInterval(t);
    }, [card.id, card.status, onChange]);

    const call = async (what: "run" | "undo" | "cancel") => {
        setBusy(what);
        setNote(null);
        try {
            const res = await api.post<{ card: ActionCard; message?: string; outcome?: string }>(
                `/api/help-desk/actions/${card.id}/${what}`,
                what === "run" && card.tier === "confirm" ? { confirm: typed } : {},
            );
            if (res.message) setNote(res.message);
            onChange(res.card);
        } catch (e) {
            const msg =
                e instanceof ApiError
                    ? ((e.body as { message?: string } | undefined)?.message ?? e.message)
                    : e instanceof Error
                      ? e.message
                      : "That didn't work.";
            setNote(msg || "That didn't work. Try again.");
        } finally {
            setBusy(null);
        }
    };

    if (card.status === "cancelled") {
        return (
            <div className="hact-off" data-testid="help-desk-action">
                {card.error ? card.error : `Cancelled: ${p.title.toLowerCase()}. Nothing changed.`}
            </div>
        );
    }

    const header =
        card.tier === "admin"
            ? `▲ Needs ${card.approver ?? "an Admin"}`
            : card.tier === "confirm"
              ? `▲ ${p.title}`
              : `● ${p.title}`;
    const confirmReady = card.tier !== "confirm" || typed.trim().replace(/^#/, "").length >= (card.confirmLength ?? 1);

    return (
        <div className="hact-wrap" data-testid="help-desk-action">
            <div className={`hact ${high ? "high" : "safe"}`}>
                <div className="hact-h">{header}</div>
                <div className="hact-b">
                    {card.tier === "admin" && <div className="hact-t">{p.title}</div>}
                    <ActionPreviewBody card={card} />
                    {!p.undoable && p.cantUndo && card.status === "proposed" && (
                        <div className="hact-note hact-warn">{p.cantUndo}</div>
                    )}
                    {card.status === "failed" && card.error && <div className="hact-err">{card.error}</div>}
                    {card.status === "proposed" && card.tier === "confirm" && (
                        <label className="hact-confirm">
                            <span>Type the job number to confirm</span>
                            <input
                                value={typed}
                                inputMode="numeric"
                                autoComplete="off"
                                aria-label="Type the job number to confirm"
                                onChange={(e) => setTyped(e.target.value)}
                            />
                        </label>
                    )}
                    {card.status === "proposed" && (
                        <div className="hact-btns">
                            <button
                                type="button"
                                className="hact-do"
                                disabled={busy !== null || !confirmReady}
                                onClick={() => void call("run")}
                            >
                                {busy === "run" ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : sendForOk ? (
                                    "Send for OK"
                                ) : card.tier === "confirm" ? (
                                    "Send it"
                                ) : (
                                    "Do it"
                                )}
                            </button>
                            <button
                                type="button"
                                className="hact-cancel"
                                disabled={busy !== null}
                                onClick={() => void call("cancel")}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {card.status === "waiting" && (
                <div className="hact-next">
                    <b>Next:</b>&nbsp;{card.approver ?? "An Admin"} sees it in the Help Desk Review list. When it&apos;s
                    OK&apos;d, this chat shows ✓ and so does My questions. You don&apos;t need to do anything else.
                </div>
            )}
            {card.status === "declined" && (
                <div className="hact-undone" role="status">
                    <span>
                        Not done{card.decidedBy ? `: ${card.decidedBy} said no` : ""}
                        {card.decisionNote ? `. "${card.decisionNote}"` : "."}
                    </span>
                </div>
            )}
            {(card.status === "done" || card.status === "undone") && (
                <div className={card.status === "done" ? "hact-done" : "hact-undone"} role="status">
                    <span>
                        {card.status === "done" ? "✓ " : "↺ "}
                        {card.result?.text ?? (card.status === "done" ? "Done." : "Put back.")}
                        {card.tier === "admin" && card.status === "done" && card.decidedBy
                            ? ` OK'd by ${card.decidedBy}.`
                            : ""}
                    </span>
                    {card.canUndo && (
                        <button
                            type="button"
                            className="hact-undo"
                            disabled={busy !== null}
                            onClick={() => void call("undo")}
                        >
                            {busy === "undo" ? "Undoing…" : "Undo"}
                        </button>
                    )}
                </div>
            )}
            {card.status === "done" && card.result?.next && (
                <div className="hact-next">
                    <b>Next:</b>&nbsp;{card.result.next}
                </div>
            )}
            {card.status === "done" && card.kind === "fix_serial" && onNext && (
                <div className="hact-follow">
                    <button type="button" className="hchip" onClick={() => onNext("Resend the packet")}>
                        Resend the packet
                    </button>
                </div>
            )}
            {note && <div className="hact-err">{note}</div>}
        </div>
    );
}
